<template>
  <main class="bh-page">
    <!-- MOBILE STICKY CTA -->
    <div class="mcta" role="navigation" aria-label="Quick actions">
      <a href="tel:6267165717" class="mc mc-w">&#128222; CALL NOW</a>
      <a href="/booking" class="mc mc-g">&#128293; BOOK NOW</a>
    </div>

    <!-- SOCIAL PROOF POPUP -->
    <div class="sp-popup" id="spPopup"></div>

    <!-- NAVIGATION -->
    <nav class="nav" id="nav" :class="{ 'mobile-active': isMobileMenuOpen }">
      <div class="nav-logo">Backyard Hibachi</div>

      <!-- 导航链接区域 -->
      <div class="nav-links" :class="{ 'is-open': isMobileMenuOpen }">
        <a href="#experience" @click="closeMobileMenu">Experience</a>
        <a href="#menu" @click="closeMobileMenu">Menu</a>
        <a href="#gallery" @click="closeMobileMenu">Gallery</a>

        <!-- Location 下拉菜单 -->
        <div class="nav-location" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
          <a class="nav-location-trigger" type="button" @click.prevent="toggleLocation">
            Location
            <ChevronDown :size="14" :class="{ 'rotate-icon': isLocationOpen }" />
          </a>
          <div class="location-menu" :class="{ 'is-active': isLocationOpen }">
            <a href="/santa-rosa-beach-hibachi" @click="closeMobileMenu">Santa Rosa Beach &amp; 30A</a>
            <a href="/inlet-beach-hibachi" @click="closeMobileMenu">Inlet &amp; Rosemary Beach</a>
            <a href="/destin-miramar-beach-hibachi" @click="closeMobileMenu">Miramar Beach &amp; Destin</a>
          </div>
        </div>
        <a href="/large-events" @click="closeMobileMenu">Large Events</a>
        <a href="#reviews" @click="closeMobileMenu">Reviews</a>
        <a href="/blog" @click="closeMobileMenu">Blog</a>

        <!-- 移动端专有的预订按钮 -->
        <a href="/booking" class="nav-cta mobile-only-cta" @click="closeMobileMenu">Book Now</a>
      </div>

      <!-- PC端右侧预订按钮 -->
      <a href="/booking" class="nav-cta desktop-only-cta">Book Now</a>

      <!-- 移动端汉堡包切换按钮 -->
      <button class="mobile-toggle" @click="toggleMobileMenu" aria-label="Toggle navigation">
        <X v-if="isMobileMenuOpen" :size="24" />
        <Menu v-else :size="24" />
      </button>
    </nav>

    <!-- HERO -->
    <section class="hero">
      <video class="hero-video" autoplay muted loop playsinline poster="/hero-poster.jpg" preload="auto">
        <source src="/hero.mp4" type="video/mp4">
      </video>
      <div class="hero-overlay"></div>
      <div class="hero-particles" id="particles"></div>
      <div class="hero-content">
        <p class="hero-jp">鉄板焼き体験</p>
        <h1 class="hero-title">Backyard<br><em>Hibachi</em></h1>
        <p class="hero-sub">Premium private teppanyaki chef brought to your Florida backyard, beach house, or vacation
          rental.</p>
        <a href="tel:6267165717" class="hero-btn">Book Your Experience</a>
      </div>
      <div class="hero-scroll">
        <span>Scroll</span>
        <div class="scroll-line"></div>
      </div>
    </section>

    <!-- EXPERIENCE -->
    <section class="section section-warm" id="experience">
      <div class="wrap">
        <div class="exp-grid">
          <div>
            <p class="section-label">The Experience</p>
            <h2 class="section-title">Not Just a Meal.<br>A Performance.</h2>
            <p class="section-desc">Our chef brings the full Japanese teppanyaki experience to your backyard. Towering
              flames, precision knife work, and unforgettable entertainment - all while the Florida breeze rolls in.</p>
            <ul class="exp-features">
              <li>Live fire show with onion volcano & flying shrimp</li>
              <li>Premium proteins - filet mignon, lobster, scallops</li>
              <li>Full setup including grill, plates & utensils</li>
              <li>Complete cleanup - you just enjoy</li>
              <li>Perfect for pools, patios & beach houses</li>
            </ul>
          </div>
          <div class="exp-video">
            <img src="/top_img_1.jpg" alt="Experience Image"
              style="width: 100%;height: 100%;object-fit: cover;object-position: center;">
          </div>
        </div>
      </div>
    </section>

    <!-- MENU -->
    <section class="section section-dark" id="menu">
      <div class="wrap">
        <p class="section-label">Menu</p>
        <h2 class="section-title">Choose Your Experience</h2>
        <p class="section-desc">Every meal includes salad, grilled vegetables, and hibachi fried rice. $500 minimum per
          event. Full setup & cleanup included.</p>
        <div class="menu-grid">
          <div class="menu-card">
            <div class="menu-price">$50<span style="font-size:.9rem;font-weight:400;color:#999">/person</span></div>
            <h3 class="menu-name">2 Protein Experience</h3>
            <ul class="menu-items">
              <li>Choose any 2 proteins per person</li>
              <li>Chicken, Steak, Shrimp, Salmon, Scallop, Tofu, Vegetables</li>
              <li>Hibachi fried rice</li>
              <li>Grilled vegetables</li>
              <li>Side salad</li>
              <li>Yum yum & teriyaki sauce</li>
              <li>Live teppanyaki fire show</li>
              <li>Full setup & cleanup</li>
            </ul>
          </div>
          <div class="menu-card">
            <div class="menu-price">$60<span style="font-size:.9rem;font-weight:400;color:#999">/person</span></div>
            <h3 class="menu-name">3 Protein Experience</h3>
            <ul class="menu-items">
              <li>Choose any 3 proteins per person</li>
              <li>Chicken, Steak, Shrimp, Salmon, Scallop, Tofu, Vegetables</li>
              <li>Hibachi fried rice</li>
              <li>Grilled vegetables</li>
              <li>Side salad</li>
              <li>Yum yum & teriyaki sauce</li>
              <li>Live teppanyaki fire show</li>
              <li>Full setup & cleanup</li>
            </ul>
          </div>
          <div class="menu-card">
            <div class="menu-price">$35<span style="font-size:.9rem;font-weight:400;color:#999">/person</span></div>
            <h3 class="menu-name">Buffet Style</h3>
            <ul class="menu-items">
              <li>Self-serve buffet setup</li>
              <li>Chicken, Steak, Shrimp, Salmon, Scallop, Tofu, Vegetables</li>
              <li>Hibachi fried rice</li>
              <li>Grilled vegetables</li>
              <li>Side salad</li>
              <li>Yum yum & teriyaki sauce</li>
              <li>Full setup & cleanup</li>
              <li>Great for large groups</li>
            </ul>
          </div>
          <div class="menu-card">
            <div class="menu-price">$30<span style="font-size:.9rem;font-weight:400;color:#999">/child</span></div>
            <h3 class="menu-name">Kids Menu</h3>
            <ul class="menu-items">
              <li>Choose 1 protein per child</li>
              <li>Chicken, Steak, Shrimp, Salmon, Tofu, Vegetables</li>
              <li>Hibachi fried rice</li>
              <li>Grilled vegetables</li>
              <li>Side salad</li>
              <li>Yum yum & teriyaki sauce</li>
              <li>Same fun fire show experience</li>
              <li>Full setup & cleanup</li>
            </ul>
          </div>
        </div>
        <div class="menu-extras">
          <div class="extras-col">
            <h4>Premium Upgrades</h4>
            <ul class="menu-items">
              <li>Filet Mignon +$10</li>
              <li>Lobster +$15</li>
            </ul>
          </div>
          <div class="extras-col">
            <h4>Side Orders</h4>
            <ul class="menu-items">
              <li>Extra Protein +$10</li>
              <li>Vegetables +$10</li>
              <li>Fried Rice +$6</li>
              <li>Noodles +$8</li>
            </ul>
          </div>
          <div class="extras-col">
            <h4>Appetizers</h4>
            <ul class="menu-items">
              <li>Edamame $20</li>
              <li>Pork Gyoza $20 (12 pcs)</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- GALLERY -->
    <section class="section section-ocean" id="gallery">
      <div class="wrap">
        <p class="section-label">Gallery</p>
        <h2 class="section-title">Moments We've Created</h2>
        <div class="gallery">
          <div class="gallery-item"><img src="/gallery-1.jpg"
              alt="Private hibachi fire show at backyard party in Destin Florida" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-2.jpg"
              alt="Teppanyaki chef cooking steak and shrimp at beach house in 30A" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-3.webp"
              alt="Hibachi chef performing onion volcano trick at Panama City Beach" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-4.webp"
              alt="Full backyard hibachi catering setup with dining arrangement NW Florida" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-5.webp"
              alt="Live hibachi performance with fire at vacation rental party" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-6.jpg"
              alt="Private hibachi dinner party at poolside in Santa Rosa Beach Florida" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-7.jpg"
              alt="Private hibachi dinner party at poolside in Santa Rosa Beach Florida" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-8.jpg"
              alt="Private hibachi dinner party at poolside in Santa Rosa Beach Florida" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-9.jpg"
              alt="Private hibachi dinner party at poolside in Santa Rosa Beach Florida" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-10.jpg"
              alt="Private hibachi dinner party at poolside in Santa Rosa Beach Florida" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-11.jpg"
              alt="Private hibachi dinner party at poolside in Santa Rosa Beach Florida" loading="lazy"></div>
          <div class="gallery-item"><img src="/gallery-12.jpg"
              alt="Private hibachi dinner party at poolside in Santa Rosa Beach Florida" loading="lazy"></div>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section class="section section-warm">
      <div class="wrap">
        <p class="section-label" style="text-align:center">How It Works</p>
        <h2 class="section-title" style="text-align:center">Simple as One, Two, Three</h2>
        <div class="steps">
          <div class="step">
            <h3>Book Your Date</h3>
            <p>Call or text us with your date, guest count, and location. We'll confirm availability and send a simple
              invoice.</p>
          </div>
          <div class="step">
            <h3>We Show Up Ready</h3>
            <p>Chef arrives 45 minutes early with everything - grill, fresh ingredients, plates, utensils, and propane.
              Table & chair setup available using your venue's furniture (+$10/person). You provide nothing.</p>
          </div>
          <div class="step">
            <h3>Enjoy the Show</h3>
            <p>Sit back with your guests, enjoy the flames, the food, and the Florida sunset. We handle everything
              including cleanup.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- REVIEWS -->
    <section class="section section-dark" id="reviews">
      <div class="wrap">
        <p class="section-label">Reviews</p>
        <h2 class="section-title">What Our Guests Say</h2>
        <div class="reviews">
          <div class="review">
            <div class="review-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
            <p class="review-text">"Absolutely incredible experience! Chef was amazing with the fire show. Our whole
              bachelorette crew was screaming. Best night of the trip!"</p>
            <p class="review-author">- Ashley T., 30A Beach House</p>
          </div>
          <div class="review">
            <div class="review-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
            <p class="review-text">"We booked for a birthday party at our PCB rental. 15 guests, everyone loved it. The
              food was restaurant quality and the show was unreal."</p>
            <p class="review-author">- Marcus W., Panama City Beach</p>
          </div>
          <div class="review">
            <div class="review-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
            <p class="review-text">"Third time booking and it gets better every time. The filet mignon and lobster tail
              are incredible. Our go-to for special occasions."</p>
            <p class="review-author">- Jennifer & Mike, Destin</p>
          </div>
        </div>
      </div>
    </section>

    <!-- SERVICE AREAS -->
    <section class="section section-ocean" id="areas">
      <div class="wrap">
        <p class="section-label">Service Areas</p>
        <h2 class="section-title">We Come to You</h2>
        <p class="section-desc">Private hibachi catering across all of Northwest Florida's most beautiful coastal
          communities.</p>
        <div class="areas-grid">
          <a href="/hibachi-catering-destin" class="area-card">
            <span class="area-name">Destin</span>
            <span class="area-sub">Crystal Beach, HarborWalk, Holiday Isle</span>
          </a>
          <a href="/hibachi-catering-30a" class="area-card">
            <span class="area-name">30A</span>
            <span class="area-sub">Rosemary Beach, Seaside, WaterColor</span>
          </a>
          <a href="/hibachi-catering-panama-city-beach" class="area-card">
            <span class="area-name">Panama City Beach</span>
            <span class="area-sub">Pier Park, Laguna Beach, Inlet Beach</span>
          </a>
          <a href="/hibachi-catering-santa-rosa-beach" class="area-card">
            <span class="area-name">Santa Rosa Beach</span>
            <span class="area-sub">Gulf Place, Blue Mountain, Grayton</span>
          </a>
          <a href="/hibachi-catering-fort-walton-beach" class="area-card">
            <span class="area-name">Fort Walton Beach</span>
            <span class="area-sub">Okaloosa Island, Mary Esther, Shalimar</span>
          </a>
          <a href="/hibachi-catering-pensacola" class="area-card">
            <span class="area-name">Pensacola</span>
            <span class="area-sub">Pensacola Beach, Gulf Breeze, Navarre</span>
          </a>
          <a href="/hibachi-catering-miramar-beach" class="area-card">
            <span class="area-name">Miramar Beach</span>
            <span class="area-sub">Sandestin, Silver Sands, Seascape</span>
          </a>
          <a href="/hibachi-catering-rosemary-beach" class="area-card">
            <span class="area-name">Rosemary Beach</span>
            <span class="area-sub">Alys Beach, Seacrest, The Preserve</span>
          </a>
          <a href="/hibachi-catering-seaside" class="area-card">
            <span class="area-name">Seaside</span>
            <span class="area-sub">WaterColor, Grayton Beach, Blue Mountain</span>
          </a>
          <a href="/hibachi-catering-navarre" class="area-card">
            <span class="area-name">Navarre</span>
            <span class="area-sub">Navarre Beach, Holley by the Sea</span>
          </a>
          <a href="/hibachi-catering-inlet-beach" class="area-card">
            <span class="area-name">Inlet Beach</span>
            <span class="area-sub">Camp Creek, Watersound Origins</span>
          </a>
          <a href="/hibachi-catering-mexico-beach" class="area-card">
            <span class="area-name">Mexico Beach</span>
            <span class="area-sub">Parker, Beacon Hill, St. Joe Beach</span>
          </a>
          <a href="/hibachi-catering-cape-san-blas" class="area-card">
            <span class="area-name">Cape San Blas</span>
            <span class="area-sub">Port St. Joe, Indian Pass, St. George Island</span>
          </a>
        </div>
      </div>
    </section>

    <!-- TRUST / STATS -->
    <section class="section section-warm">
      <div class="wrap">
        <div class="stats-row">
          <div class="stat">
            <span class="stat-num">500+</span>
            <span class="stat-label">Events Served</span>
          </div>
          <div class="stat">
            <span class="stat-num">5.0</span>
            <span class="stat-label">Star Rating</span>
          </div>
          <div class="stat">
            <span class="stat-num">8+</span>
            <span class="stat-label">Years Experience</span>
          </div>
          <div class="stat">
            <span class="stat-num">100%</span>
            <span class="stat-label">Setup Included</span>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="section section-dark" id="faq">
      <div class="wrap">
        <p class="section-label">FAQ</p>
        <h2 class="section-title">Common Questions</h2>
        <div class="faq-list">
          <details class="faq-item">
            <summary>What do I need to provide?</summary>
            <p>We bring the grill, propane, plates, utensils, napkins, all ingredients, sauces, and condiments. You just
              provide the guests and the backyard.</p>
          </details>
          <details class="faq-item">
            <summary>What's the minimum order?</summary>
            <p>There's a $500 minimum per event. $50/person (2 proteins), $60/person (3 proteins), $35/person Buffet
              style, $30/child (1 protein). Every meal includes salad, vegetables, and fried rice.</p>
          </details>
          <details class="faq-item">
            <summary>How much space do you need?</summary>
            <p>We need roughly a 10x10 ft flat area for the grill setup. A patio, driveway, pool deck, or backyard all
              work perfectly. We can set up your venue's tables & chairs for an additional $10/person (setup & carrying
              fee).</p>
          </details>
          <details class="faq-item">
            <summary>What if it rains?</summary>
            <p>We cook rain or shine! We just need a covered area - a garage, patio cover, tent, or large covered porch
              works great. We'll coordinate with you ahead of time.</p>
          </details>
          <details class="faq-item">
            <summary>How far in advance should I book?</summary>
            <p>Weekends fill up fast, especially during peak season (March-October). We recommend booking 1-2 weeks in
              advance. Weekday events are easier to accommodate on short notice.</p>
          </details>
          <details class="faq-item">
            <summary>Do you serve at vacation rentals and Airbnbs?</summary>
            <p>Absolutely! This is one of our most popular requests. We serve at beach houses, vacation rentals,
              Airbnbs, and VRBOs all along the Florida coast. We handle everything so there's no damage or mess.</p>
          </details>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta-section">
      <div class="cta-jp">裏庭鉄板焼</div>
      <div class="wrap" style="position:relative">
        <p class="section-label" style="justify-content:center">Ready?</p>
        <h2 class="cta-title">Your Backyard.<br>Our Fire.</h2>
        <p style="color:rgba(255,255,255,.45);margin-top:1rem;font-size:.95rem">$500 minimum per event. Serving all of
          Northwest Florida.</p>
        <div style="display:flex;gap:1rem;justify-content:center;align-items:center;margin-top:2.5rem;flex-wrap:wrap">
          <a href="tel:6267165717" class="cta-phone">(626) 716-5717</a>
          <a href="/booking" class="cta-book">Book Online</a>
        </div>
        <p style="color:rgba(255,255,255,.25);margin-top:1.5rem;font-size:.82rem;letter-spacing:.05em">Call, text, or
          book online. Weekend slots fill fast.</p>
      </div>
    </section>

    <!-- GEO: AI Search Engine Optimization - Entity Declaration -->
    <section class="section section-dark" style="border-top:1px solid rgba(212,165,116,.06)">
      <div class="wrap">
        <p class="section-label" style="justify-content:center">About Us</p>
        <h2 class="section-title" style="text-align:center">Backyard Hibachi — NW Florida's Premium Private Hibachi
          Catering</h2>
        <div style="max-width:720px;margin:0 auto;font-size:.85rem;line-height:2;color:rgba(255,255,255,.45)">
          <p><strong style="color:#fff">Backyard Hibachi</strong> is a 500+ events served mobile hibachi catering
            company based in <strong style="color:#fff">Northwest Florida</strong>. We provide all-inclusive private
            hibachi chef experiences across the entire Emerald Coast, including Destin, 30A (Rosemary Beach, Seaside,
            WaterColor, WaterSound), Panama City Beach, Fort Walton Beach, Miramar Beach, Santa Rosa Beach, Navarre,
            Pensacola, Niceville, and surrounding areas.</p>
          <p>Our professionally trained teppanyaki chef brings a complete hibachi experience to your home, vacation
            rental, or event venue. Our all-inclusive service starts at <strong style="color:#fff">$50 per
              person</strong> and includes the grill, propane, all food, live fire show entertainment, plates, utensils,
            napkins, full setup, and complete cleanup. $500 minimum per event.</p>
          <p>We specialize in birthday parties, weddings, corporate events, bachelorette celebrations, graduation
            parties, and vacation rental experiences. We maintain a <strong style="color:#fff">5.0-star rating</strong>
            with 500+ events served across Northwest Florida.</p>
          <p><strong style="color:#fff">Contact:</strong> (626) 716-5717 &middot; <a
              href="https://www.backyardhibachipro.com" style="color:var(--gold)">www.backyardhibachipro.com</a></p>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <p class="footer-brand">Backyard Hibachi</p>
      <div class="footer-links">
        <a href="tel:6267165717">(626) 716-5717</a>
        <a href="https://www.instagram.com/backyardhibachipro" target="_blank" rel="noopener">Instagram</a>
        <a href="https://www.backyardhibachipro.com">www.backyardhibachipro.com</a>
      </div>
      <p>&copy; 2026 Backyard Hibachi. Premium Private Chef Experience. Florida.</p>
    </footer>
  </main>
</template>

<script setup>
import { ChevronDown, Menu, X } from 'lucide-vue-next';
import { ref } from 'vue'

const isLocationOpen = ref(false)
let timer = null

// 判断是否为移动端环境（对应 900px 断点）
const isMobile = () => {
  return window.innerWidth <= 900
}

// 鼠标移入（仅在 PC 端生效）
const handleMouseEnter = () => {
  if (isMobile()) return
  if (timer) clearTimeout(timer)
  isLocationOpen.value = true
}

// 鼠标移出（仅在 PC 端生效）
const handleMouseLeave = () => {
  if (isMobile()) return
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    isLocationOpen.value = false
  }, 100)
}

// 点击触发（移动端点击切换，PC 端也可以作为后备逻辑）
const toggleLocation = () => {
  isLocationOpen.value = !isLocationOpen.value
}

// 控制移动端汉堡菜单展开/收起
const isMobileMenuOpen = ref(false)

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  isLocationOpen.value = false // 关闭外层抽屉时同步收起二级菜单
}
</script>

<style scoped>
/* ===== RESET & BASE ===== */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0
}

.bh-page {
  font-family: 'Inter', sans-serif;
  background: #080808;
  color: #e8e4df;
  line-height: 1.7;
  overflow-x: hidden
}

img,
video {
  max-width: 100%;
  display: block
}

a {
  color: inherit;
  text-decoration: none
}

/* ===== JAPANESE TYPOGRAPHY ===== */
.jp {
  font-family: 'Noto Serif JP', serif;
  font-weight: 400
}

.jp-accent {
  font-family: 'Noto Serif JP', serif;
  color: #d4a574;
  letter-spacing: .08em
}

/* ===== COLORS - Refined Japanese + Coastal ===== */
.bh-page {
  --gold: #d4a574;
  --gold-light: #f0d4a8;
  --gold-dark: #8b6b47;
  --ocean: #6ba3b5;
  --ocean-dark: #2a4a5a;
  --sand: #f5efe6;
  --ink: #0a0a0a;
  --paper: #faf7f2;
  --ember: #e8734a;
}

/* ===== NAVIGATION ===== */
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 1.2rem 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  transition: all .4s cubic-bezier(.4, 0, .2, 1);
}

.nav.scrolled {
  background: rgba(8, 8, 8, .92);
  backdrop-filter: blur(20px);
  padding: 1rem 2.5rem;
  box-shadow: 0 1px 0 rgba(212, 165, 116, .1)
}

.nav-logo {
  font-family: 'Noto Serif JP', serif;
  font-size: 1.2rem;
  color: var(--gold);
  font-weight: 700;
  letter-spacing: .12em
}

.nav-links {
  display: flex;
  gap: 2.5rem;
  font-size: .75rem;
  letter-spacing: .18em;
  text-transform: uppercase;
  font-weight: 400
}

.nav-links a {
  color: rgba(255, 255, 255, .6);
  transition: color .3s;
  position: relative
}

.nav-links a:hover {
  color: var(--gold)
}

.nav-links a::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 1px;
  background: var(--gold);
  transition: width .3s
}

.nav-links a:hover::after {
  width: 100%
}

.nav-location {
  position: relative;
}

.nav-location-trigger {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: rgba(255, 255, 255, .6);
  font: inherit;
  transition: color .3s;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.nav-location-trigger svg {
  transition: transform 0.3s ease;
}

.nav-location-trigger svg.rotate-icon {
  transform: rotate(180deg);
}

.nav-location:hover .nav-location-trigger,
.nav-location:focus-within .nav-location-trigger {
  color: var(--gold);
}

.location-menu {
  position: absolute;
  top: calc(100% + 1rem);
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  padding: .55rem;
  background: rgba(12, 12, 12, .98);
  border: 1px solid rgba(212, 165, 116, .24);
  box-shadow: 0 24px 54px rgba(0, 0, 0, .48);
  text-align: left;
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, 8px);
  transition: opacity .2s, transform .2s;
}

.location-menu.is-active {
  opacity: 1;
  pointer-events: auto;
  transform: translate(-50%, 0);
}

.location-menu a {
  display: block;
  padding: .8rem .9rem;
  letter-spacing: .06em;
  text-transform: none;
}

.location-menu a::after {
  display: none;
}

.location-menu a:hover {
  background: rgba(212, 165, 116, .1);
}

.nav-cta {
  padding: .7rem 1.8rem;
  border: 1px solid rgba(212, 165, 116, .5);
  color: var(--gold);
  font-size: .7rem;
  letter-spacing: .2em;
  text-transform: uppercase;
  transition: all .3s;
  border-radius: 0;
}

.nav-cta:hover {
  background: var(--gold);
  color: #000;
  border-color: var(--gold)
}

/* ===== HERO ===== */
.hero {
  position: relative;
  height: 100vh;
  min-height: 700px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.hero-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: .5;
  filter: brightness(.9) saturate(1.1);
  will-change: transform;
  background: #080808;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 30% 50%, rgba(212, 165, 116, .06) 0%, transparent 50%),
    linear-gradient(180deg, rgba(8, 8, 8, .2) 0%, rgba(8, 8, 8, .4) 50%, rgba(8, 8, 8, .85) 100%);
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 2rem;
  max-width: 900px;
}

.hero-jp {
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(.85rem, 1.5vw, 1.1rem);
  color: var(--gold);
  letter-spacing: .5em;
  margin-bottom: 2rem;
  opacity: .8;
  font-weight: 200;
}

.hero-title {
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(2.8rem, 7vw, 5.5rem);
  font-weight: 900;
  line-height: 1.05;
  margin-bottom: 1.5rem;
  color: #fff;
  text-shadow: 0 4px 60px rgba(0, 0, 0, .5);
  letter-spacing: -.02em;
}

.hero-title em {
  font-style: normal;
  color: var(--gold);
  font-weight: 700
}

.hero-sub {
  font-size: clamp(.9rem, 1.3vw, 1.05rem);
  color: rgba(255, 255, 255, .7);
  max-width: 520px;
  margin: 0 auto 3rem;
  font-weight: 300;
  letter-spacing: .03em;
  line-height: 1.8;
}

.hero-btn {
  display: inline-block;
  padding: 1.1rem 3.5rem;
  background: transparent;
  color: #fff;
  font-weight: 500;
  font-size: .8rem;
  letter-spacing: .2em;
  text-transform: uppercase;
  transition: all .4s;
  border: 1px solid rgba(255, 255, 255, .4);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.hero-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--gold);
  transform: translateY(100%);
  transition: transform .4s cubic-bezier(.4, 0, .2, 1);
  z-index: -1;
}

.hero-btn:hover {
  color: #000;
  border-color: var(--gold)
}

.hero-btn:hover::before {
  transform: translateY(0)
}

.hero-scroll {
  position: absolute;
  bottom: 3rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .5rem;
}

.hero-scroll span {
  color: rgba(255, 255, 255, .4);
  font-size: .65rem;
  letter-spacing: .3em;
  text-transform: uppercase
}

.scroll-line {
  width: 1px;
  height: 40px;
  background: linear-gradient(180deg, var(--gold), transparent);
  animation: scrollPulse 2s ease-in-out infinite;
}

@keyframes scrollPulse {

  0%,
  100% {
    opacity: .3;
    transform: scaleY(.6)
  }

  50% {
    opacity: 1;
    transform: scaleY(1)
  }
}

.hero-particles {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
}

/* ===== SECTIONS ===== */
.section {
  padding: 7rem 2rem
}

.section-dark {
  background: #080808
}

.section-warm {
  background: linear-gradient(160deg, #0c0c0c 0%, #14100c 50%, #0c0c0c 100%)
}

.section-ocean {
  background: linear-gradient(160deg, #080808 0%, #0a1318 50%, #080808 100%)
}

.wrap {
  max-width: 1100px;
  margin: 0 auto
}

.section-label {
  font-family: 'Noto Serif JP', serif;
  font-size: .7rem;
  color: var(--gold);
  letter-spacing: .4em;
  text-transform: uppercase;
  margin-bottom: .8rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.section-label::before {
  content: '';
  width: 30px;
  height: 1px;
  background: var(--gold);
  opacity: .5
}

.section-title {
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.15;
  letter-spacing: -.01em;
}

.section-desc {
  color: rgba(255, 255, 255, .5);
  max-width: 600px;
  font-size: .92rem;
  line-height: 1.9
}

/* ===== EXPERIENCE SECTION ===== */
.exp-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
  margin-top: 4rem;
}

.exp-video {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 30px 80px rgba(0, 0, 0, .6), 0 0 0 1px rgba(212, 165, 116, .1);
  aspect-ratio: 9/16;
  max-height: 550px;
  position: relative;
}

.exp-features {
  list-style: none;
  margin-top: 2.5rem
}

.exp-features li {
  padding: 1.1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, .04);
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-size: .92rem;
  color: rgba(255, 255, 255, .75);
}

.exp-features li::before {
  content: '';
  width: 6px;
  height: 6px;
  background: var(--gold);
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 8px rgba(212, 165, 116, .4);
}

/* ===== MENU SECTION ===== */
.menu-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: 3.5rem;
}

@media(max-width:1000px) {
  .menu-grid {
    grid-template-columns: repeat(2, 1fr)
  }
}

.menu-card {
  background: linear-gradient(160deg, rgba(255, 255, 255, .03), rgba(255, 255, 255, .01));
  border: 1px solid rgba(255, 255, 255, .05);
  border-radius: 16px;
  padding: 2.8rem;
  transition: all .4s cubic-bezier(.4, 0, .2, 1);
  position: relative;
  overflow: hidden;
}

.menu-price {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--gold);
  margin-bottom: .8rem;
}

.menu-name {
  font-family: 'Noto Serif JP', serif;
  font-size: 1.15rem;
  margin-bottom: 1.2rem;
  color: rgba(255, 255, 255, .9);
}

.menu-items {
  list-style: none;
  color: rgba(255, 255, 255, .5);
  font-size: .88rem;
}

.menu-items li {
  padding: .5rem 0;
  padding-left: 1.5rem;
  position: relative;
}

.menu-items li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 8px;
  height: 1px;
  background: var(--gold);
  opacity: .5
}

.menu-extras {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
  padding: 2.5rem;
  background: rgba(255, 255, 255, .02);
  border: 1px solid rgba(255, 255, 255, .04);
  border-radius: 16px
}

.extras-col h4 {
  font-family: 'Noto Serif JP', serif;
  font-size: .9rem;
  color: var(--gold);
  margin-bottom: 1rem;
  font-weight: 400
}

/* ===== GALLERY ===== */
.gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-top: 3.5rem;
  border-radius: 16px;
  overflow: hidden;
}

.gallery-item {
  aspect-ratio: 1;
  overflow: hidden;
  position: relative;
  cursor: pointer;
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform .6s cubic-bezier(.4, 0, .2, 1);
  filter: brightness(.85)
}

.gallery-item:hover img {
  transform: scale(1.08);
  filter: brightness(1)
}

/* ===== HOW IT WORKS ===== */
.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 3rem;
  margin-top: 4rem;
  counter-reset: step;
}

.step {
  text-align: center;
  counter-increment: step;
  padding: 2.5rem 1.5rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, .02);
  border: 1px solid rgba(255, 255, 255, .04);
}

.step::before {
  content: counter(step, cjk-ideographic);
  font-family: 'Noto Serif JP', serif;
  font-size: 2.5rem;
  color: var(--gold);
  opacity: .3;
  display: block;
  margin-bottom: 1.2rem;
}

.step h3 {
  font-family: 'Noto Serif JP', serif;
  font-size: 1.05rem;
  margin-bottom: .8rem;
  color: rgba(255, 255, 255, .9)
}

.step p {
  color: rgba(255, 255, 255, .45);
  font-size: .88rem;
  line-height: 1.8
}

/* ===== TESTIMONIALS ===== */
.reviews {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 3.5rem
}

.review {
  background: rgba(255, 255, 255, .02);
  border: 1px solid rgba(255, 255, 255, .04);
  border-radius: 16px;
  padding: 2.5rem;
}

.review-stars {
  color: var(--gold);
  font-size: 1rem;
  margin-bottom: 1.2rem;
}

.review-text {
  color: rgba(255, 255, 255, .7);
  font-size: .9rem;
  line-height: 1.9;
  font-style: italic;
  margin-bottom: 1.5rem
}

.review-author {
  font-size: .8rem;
  color: var(--gold);
}

/* ===== CTA SECTION ===== */
.cta-section {
  text-align: center;
  padding: 10rem 2rem;
  background: #080808;
  position: relative;
  overflow: hidden;
}

.cta-jp {
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(2rem, 8vw, 6rem);
  color: rgba(212, 165, 116, .04);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  white-space: nowrap;
  pointer-events: none;
}

.cta-title {
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(2.2rem, 5vw, 3.8rem);
  margin-bottom: 1rem;
  font-weight: 700;
}

.cta-phone {
  display: inline-block;
  margin-top: 2.5rem;
  padding: 1.3rem 3.5rem;
  background: var(--gold);
  color: #000;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all .4s;
}

/* ===== FOOTER ===== */
.footer {
  padding: 4rem 2rem 3rem;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, .04);
  font-size: .78rem;
  color: rgba(255, 255, 255, .3);
}

.footer-brand {
  font-family: 'Noto Serif JP', serif;
  color: var(--gold);
  font-size: 1rem;
  margin-bottom: 1.2rem;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 2.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap
}

.footer-links a {
  color: rgba(255, 255, 255, .4);
}

/* ===== SERVICE AREAS ===== */
.areas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 3rem;
}

.area-card {
  display: block;
  padding: 1.8rem 2rem;
  background: rgba(255, 255, 255, .02);
  border: 1px solid rgba(255, 255, 255, .05);
  border-radius: 12px;
  transition: all .3s;
}

.area-name {
  display: block;
  font-family: 'Noto Serif JP', serif;
  font-size: 1.1rem;
  color: #fff;
  margin-bottom: .3rem
}

.area-sub {
  font-size: .8rem;
  color: rgba(255, 255, 255, .4);
}

/* ===== STATS ROW ===== */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  text-align: center;
  padding: 2rem 0;
}

.stat-num {
  display: block;
  font-family: 'Noto Serif JP', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: var(--gold);
  margin-bottom: .5rem;
}

.stat-label {
  font-size: .8rem;
  color: rgba(255, 255, 255, .4);
}

/* ===== FAQ ===== */
.faq-list {
  max-width: 750px;
  margin-top: 2.5rem
}

.faq-item {
  border-bottom: 1px solid rgba(255, 255, 255, .05);
}

.faq-item summary {
  padding: 1.4rem 0;
  font-size: .95rem;
  cursor: pointer;
  color: rgba(255, 255, 255, .85);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.faq-item summary::after {
  content: '+';
  font-size: 1.3rem;
  color: var(--gold);
  opacity: .5;
}

.faq-item[open] summary::after {
  transform: rotate(45deg)
}

.faq-item p {
  padding: 0 0 1.5rem;
  font-size: .88rem;
  color: rgba(255, 255, 255, .5);
  line-height: 1.8;
}

/* ===== CTA BOOK BUTTON ===== */
.cta-book {
  display: inline-block;
  padding: 1.3rem 3.5rem;
  border: 1px solid rgba(212, 165, 116, .5);
  color: var(--gold);
  font-weight: 600;
  font-size: .9rem;
}

/* ===== MOBILE STICKY CTA ===== */
.mcta {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
  display: flex;
  background: #080808;
  border-top: 2px solid var(--gold)
}

.mc {
  flex: 1;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  font-weight: 800;
  font-size: .78rem;
  min-height: 54px
}

.mc-w {
  background: #fff;
  color: #080808
}

.mc-g {
  background: var(--gold);
  color: #000
}

@media(min-width:901px) {
  .mcta {
    display: none
  }
}

@media(max-width:900px) {
  .bh-page {
    padding-bottom: 54px
  }
}

/* ===== SOCIAL PROOF POPUP ===== */
.sp-popup {
  position: fixed;
  bottom: 70px;
  left: 16px;
  z-index: 998;
  background: rgba(20, 20, 20, .95);
  border: 1px solid rgba(212, 165, 116, .15);
  border-radius: 12px;
  padding: 12px 18px;
  font-size: .82rem;
  color: #ccc;
  transform: translateX(-120%);
  transition: transform .5s cubic-bezier(.4, 0, .2, 1);
  max-width: 300px;
}

.mobile-toggle {
  display: none;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 101;
}

.mobile-only-cta {
  display: none;
}

/* ===== RESPONSIVE MEDIA QUERIES ===== */
@media (max-width: 900px) {
  .desktop-only-cta {
    display: none;
  }

  .mobile-toggle {
    display: block;
  }

  /* 移动端菜单抽屉 */
  .nav-links {
    position: fixed;
    top: 0;
    right: 0;
    width: 80%;
    max-width: 320px;
    height: 100vh;
    height: 100svh;
    background: rgba(12, 12, 12, 0.98);
    backdrop-filter: blur(20px);
    border-left: 1px solid rgba(212, 165, 116, 0.2);
    flex-direction: column;
    align-items: flex-start;
    padding: 3rem 2rem;
    gap: 1.8rem;
    font-size: 0.9rem;
    transform: translateX(100%);
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.8);
    overflow-y: auto;
  }

  .nav-links.is-open {
    transform: translateX(0);
  }

  .mobile-only-cta {
    display: inline-block;
    margin-top: 1rem;
    width: 100%;
    text-align: center;
  }

  /* 移动端 Location 下拉菜单适配 */
  .nav-location {
    width: 100%;
  }

  .location-menu {
    position: static;
    transform: none !important;
    width: 100%;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(212, 165, 116, 0.15);
    margin-top: 0.8rem;
    display: none;
    opacity: 1;
    pointer-events: auto;
    box-shadow: none;
  }

  .location-menu.is-active {
    display: block;
  }

  .exp-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }

  .gallery {
    grid-template-columns: repeat(2, 1fr);
  }

  .steps {
    grid-template-columns: 1fr;
  }

  .menu-grid {
    grid-template-columns: 1fr;
  }

  .menu-extras {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 1.5rem;
  }

  .areas-grid {
    grid-template-columns: 1fr;
  }

  .stats-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem 1rem;
  }
}
</style>